import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LandingShell } from '../landing/LandingShell'
import type { PublicMarketingRoute } from '../../seo/routes'
import { PageSEO } from './PageSEO'

export interface EvergreenSection {
  heading: string
  paragraphs: readonly string[]
}

type EvergreenRoute = Exclude<
  PublicMarketingRoute,
  '/' | '/pricing' | '/contact' | '/terms' | '/privacy' | '/refund' | '/cookies'
>

interface EvergreenPageProps {
  path: EvergreenRoute
  title: string
  lead: string
  sections: readonly EvergreenSection[]
  children?: ReactNode
  jsonLd?: object | object[]
}

export function EvergreenPage({
  path,
  title,
  lead,
  sections,
  children,
  jsonLd,
}: EvergreenPageProps) {
  return (
    <LandingShell>
      <PageSEO path={path} jsonLd={jsonLd} />
      <article className="px-4 pb-16 pt-4 sm:px-8 sm:pb-24 sm:pt-8">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">{title}</h1>
          <p className="mt-4 text-base leading-relaxed text-text-secondary sm:text-lg">{lead}</p>

          <div className="mt-10 space-y-10">
            {sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-xl font-semibold text-text-primary">{section.heading}</h2>
                <div className="mt-3 space-y-3">
                  {section.paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-base leading-relaxed text-text-secondary">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {children}

          <p className="mt-12 text-sm text-text-secondary">
            <Link to="/signup" className="text-accent hover:underline">
              Start free
            </Link>
            {' · '}
            <Link to="/pricing" className="text-accent hover:underline">
              Pricing
            </Link>
            {' · '}
            <Link to="/" className="text-accent hover:underline">
              Home
            </Link>
          </p>
        </div>
      </article>
    </LandingShell>
  )
}
